// The field vocabulary of a profile: the six things the daily target is
// worked out from, and the target itself. Shared by the storage shape
// (./data), the public shape (./public), the calculator (./target), the api's
// model layer and the app.
//
// Metric throughout — kg and cm — because that is what the formula is written
// in and what the people using this weigh themselves in.
export const orgId = { type: "string" };

export const age = { label: "Age", type: "integer", minimum: 13, maximum: 120 };

// What the formula has a constant for. It has two, one for men and one for
// women; "other" uses the midpoint between them (see ./target), so nobody has
// to pick a constant that does not describe them to get a number.
export const GENDERS = ["female", "male", "other"];

export const gender = {
  label: "Gender",
  type: "string",
  enum: GENDERS,
  enumLabels: [{ label: "Female" }, { label: "Male" }, { label: "Other" }],
};

export const weight = { label: "Weight (kg)", type: "number", minimum: 20, maximum: 400 };
export const height = { label: "Height (cm)", type: "number", minimum: 100, maximum: 250 };

// The usual five activity levels, least to most. Their multipliers are in
// ./target, beside the formula they multiply.
export const ACTIVITIES = ["sedentary", "light", "moderate", "active", "very"];

export const activity = {
  label: "Activity",
  type: "string",
  enum: ACTIVITIES,
  enumLabels: [
    { label: "Sedentary — little or no exercise" },
    { label: "Light — exercise 1–3 days a week" },
    { label: "Moderate — exercise 3–5 days a week" },
    { label: "Active — hard exercise 6–7 days a week" },
    { label: "Very active — physical job or training twice a day" },
  ],
};

export const GOALS = ["lose", "maintain", "gain"];

export const goal = {
  label: "Goal",
  type: "string",
  enum: GOALS,
  enumLabels: [
    { label: "Lose weight — about 0.5 kg a week" },
    { label: "Keep my weight" },
    { label: "Gain weight — about 0.5 kg a week" },
  ],
};

// The bounds a daily target may take, whether calculated or typed in. The
// upper one is an entry's own calorie bound, so a target is never a number no
// single day could be checked against.
export const TARGET_MIN = 800;
export const TARGET_MAX = 10000;

// A number the person typed instead of the calculated one, or null to use the
// calculator's. Kept apart from `target` so the screen can say which it is,
// and so changing a weight later moves a calculated target and leaves a chosen
// one alone.
export const customTarget = { label: "My own target (kcal)", type: ["number", "null"], minimum: TARGET_MIN, maximum: TARGET_MAX };

// The daily target in kcal, as it was when the profile was saved: the custom
// one if there is one, the calculated one otherwise. Stored rather than
// recomputed on read so a change to the calculator never moves a target
// somebody has already set.
export const target = { type: "number", minimum: TARGET_MIN, maximum: TARGET_MAX };

// MACRO TARGETS. Grams a day of each, or null for none: optional, each on its
// own, and typed in rather than worked out — the screen offers a split of the
// calorie target to start from (./target macroSuggestion), but what is stored
// is what the person chose. The day and the chat measure each macro against
// its own target when there is one.
export const MACROS = ["protein", "carbs", "fat"];

export const MACRO_MAX = 1000;

const grams = (label) => ({ label, type: ["number", "null"], minimum: 0, maximum: MACRO_MAX });

export const proteinTarget = grams("Protein (g)");
export const carbsTarget = grams("Carbs (g)");
export const fatTarget = grams("Fat (g)");

// "protein" → "proteinTarget": the profile field a macro's target is kept in.
export const macroField = (macro) => `${macro}Target`;
